import {Component, inject, OnInit} from '@angular/core';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {PageHeaderComponent} from '../../../../../../shared/components/common/page-header/page-header.component';
import {Card} from '../../../../../../shared/components/common/card/card';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ErrorService} from '../../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {ZonasService} from '../zonas.service';
import {LabelComponent} from '../../../../../../shared/components/form/label/label.component';
import {CreationButtons} from '../../../../../../shared/components/ui/creation-buttons/creation-buttons';
import {SelectFieldComponent} from '@shared/components/form/select/select-field.component';
import {ToSelectOptionsPipe} from '@shared/pipes/to-select-options.pipe';
import {Ciudad, Departamento, Pais} from '../../../../../../shared/services/interfaces/combos.interface';
import {CombosService} from '../../../../../../shared/services/combos.service';
import {EditionButtons} from '../../../../../../shared/components/ui/edition-buttons/edition-buttons';
import {Spinner} from '../../../../../../shared/components/ui/spinner/spinner';
import {UbicacionService} from '../../ubicacion.service';
import {forkJoin} from 'rxjs';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';

@Component({
  selector: 'app-crear-zona',
  imports: [
    HasPermissionDirective,
    PageHeaderComponent,
    Card,
    ReactiveFormsModule,
    LabelComponent,
    CreationButtons,
    EditionButtons,
    Spinner,
    SelectFieldComponent,
    ToSelectOptionsPipe,
    InputFieldComponent,
    AppFormComponent,
  ],
  templateUrl: './crear-zona.html',
  styles: ``
})
export class CrearZona implements OnInit {

  saveForm!: FormGroup;
  loading = false;
  loadingData = false;
  isEditMode = false;
  zonaId: number | null = null;

  paises: Pais[] = [];
  departamentos: Departamento[] = [];
  ciudades: Ciudad[] = [];

  private fB = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private zonasService = inject(ZonasService);
  private combosService = inject(CombosService);
  private ubicacionService = inject(UbicacionService);

  ngOnInit(): void {
    this.checkEditMode();
    this.makeForm();
    this.loadPaises();
  }

  private checkEditMode(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.isEditMode = true;
      this.zonaId = parseInt(id, 10);
      this.loadZona();
    }
  }

  private loadZona(): void {
    if (!this.zonaId) return;

    this.loadingData = true;
    this.zonasService.show(this.zonaId).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          const zona = response.data;

          // Necesitamos cargar la ciudad para obtener el departamento y país
          this.loadCiudadDetails(zona.ubi_ciudad_id, zona);
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
        this.goBack();
      }
    }).add(() => {
      this.loadingData = false;
    });
  }

  private loadCiudadDetails(ciudadId: number, zona: any): void {
    // Obtener la ciudad con sus relaciones (departamento y país)
    this.ubicacionService.getCiudad(ciudadId).subscribe({
      next: (ciudadResponse) => {
        if (ciudadResponse.status && ciudadResponse.data) {
          const ciudad = ciudadResponse.data;
          const departamentoId = ciudad.ubi_departamento_id;

          // Obtener el departamento para saber el país
          this.ubicacionService.getDepartamento(departamentoId).subscribe({
            next: (deptoResponse) => {
              if (deptoResponse.status && deptoResponse.data) {
                const departamento = deptoResponse.data;
                const paisId = departamento.ubi_pais_id;

                // Cargar los combos en cascada
                const paises$ = this.combosService.getPaisesCombo();
                const departamentos$ = this.combosService.getDepartamentosCombo(paisId);
                const ciudades$ = this.combosService.getCiudadesCombo(departamentoId);

                forkJoin([paises$, departamentos$, ciudades$]).subscribe({
                  next: ([paisesRes, deptosRes, ciudadesRes]) => {
                    this.paises = paisesRes.data;
                    this.departamentos = deptosRes.data;
                    this.ciudades = ciudadesRes.data;

                    // Establecer los valores en el formulario
                    this.saveForm.patchValue({
                      nombre: zona.nombre,
                      ubi_pais_id: paisId,
                      ubi_departamento_id: departamentoId,
                      ubi_ciudad_id: ciudadId,
                    });
                  },
                  error: (error) => {
                    this.errorService.handleError(error);
                  }
                });
              }
            },
            error: (error) => {
              this.errorService.handleError(error);
            }
          });
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private loadPaises(): void {
    this.combosService.getPaisesCombo().subscribe({
      next: (response) => {
        if (response.status) {
          this.paises = response.data;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private loadDepartamentos(paisId: number): void {
    this.combosService.getDepartamentosCombo(paisId).subscribe({
      next: (response) => {
        if (response.status) {
          this.departamentos = response.data;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private loadCiudades(departamentoId: number): void {
    this.combosService.getCiudadesCombo(departamentoId).subscribe({
      next: (response) => {
        if (response.status) {
          this.ciudades = response.data;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private makeForm(): void {
    this.saveForm = this.fB.group({
      nombre: ['', Validators.required],
      ubi_ciudad_id: ['', Validators.required],
      // Helper fields for cascading selects
      ubi_pais_id: ['', Validators.required],
      ubi_departamento_id: ['', Validators.required],
    });

    this.setupCascadingSelects();
  }

  private setupCascadingSelects(): void {
    // When pais changes, load departamentos and clear ciudad
    this.saveForm.get('ubi_pais_id')?.valueChanges.subscribe((paisId) => {
      if (paisId) {
        this.loadDepartamentos(paisId);
        this.saveForm.patchValue({
          ubi_departamento_id: '',
          ubi_ciudad_id: ''
        });
        this.ciudades = [];
      }
    });

    // When departamento changes, load ciudades
    this.saveForm.get('ubi_departamento_id')?.valueChanges.subscribe((departamentoId) => {
      if (departamentoId) {
        this.loadCiudades(departamentoId);
        this.saveForm.patchValue({
          ubi_ciudad_id: ''
        });
      }
    });
  }

  saveZona(): void {
    if (this.saveForm.invalid) {
      this.saveForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const payload = {
      nombre: this.saveForm.value.nombre,
      ubi_ciudad_id: this.saveForm.value.ubi_ciudad_id,
    };

    const request = this.isEditMode && this.zonaId
      ? this.zonasService.update(this.zonaId, payload)
      : this.zonasService.store(payload);

    request.subscribe({
      next: (response) => {
        if (response.status) {
          const message = this.isEditMode ? 'Zona actualizada correctamente' : 'Zona creada correctamente';
          this.snackbarService.showSnackBar(message);
          this.goBack();
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  goBack(): void {
    this.router.navigate(['../'], {relativeTo: this.route});
  }
}
